// ============================================================================
// USER STORIES WIDGET - Displays extracted user stories in card layout
// Same visual style as RequirementsPanel story cards
// ============================================================================

import React from 'react';
import { BookOpen, CheckCircle } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const priorityStyle = (priority: string, isDark: boolean) => {
  switch (priority) {
    case 'high':
      return {
        stripe: isDark ? 'bg-red-400' : 'bg-red-500',
        badge: isDark ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700',
        label: 'High',
      };
    case 'medium':
      return {
        stripe: isDark ? 'bg-amber-400' : 'bg-amber-500',
        badge: isDark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700',
        label: 'Medium',
      };
    default:
      return {
        stripe: isDark ? 'bg-slate-500' : 'bg-slate-300',
        badge: isDark ? 'bg-white/10 text-white/50' : 'bg-slate-100 text-slate-600',
        label: 'Low',
      };
  }
};

const UserStoriesWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  const stories = state?.requirements?.userStories ?? [];

  if (!state) return null;

  if (stories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
          <BookOpen size={20} className={isDark ? 'text-white/20' : 'text-slate-300'} />
        </div>
        <p className={`text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
          No user stories extracted yet
        </p>
      </div>
    );
  }

  // Sort: high first, then medium, then low
  const sorted = [...stories].sort((a, b) => {
    const po: Record<string, number> = { high: 0, medium: 1, low: 2 };
    return (po[a.priority] || 2) - (po[b.priority] || 2);
  });

  return (
    <div className="h-full overflow-y-auto p-3 space-y-2">
      {sorted.map(story => {
        const ps = priorityStyle(story.priority, isDark);
        const personaInitials = story.persona.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
        return (
          <div
            key={story.id}
            className={`rounded-xl overflow-hidden relative flex items-start gap-3 p-4 ${
              isDark
                ? 'bg-white/[0.04] border border-white/10'
                : 'bg-white border border-slate-200 shadow-sm'
            } ${story.priority === 'low' ? 'opacity-70' : ''}`}
          >
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${ps.stripe}`} />
            <div className="flex-1 pl-2 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className={`px-2 py-0.5 rounded text-xs uppercase font-bold tracking-wider ${ps.badge}`}>
                  {ps.label}
                </span>
                <span className={`text-sm ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                  {story.persona}
                </span>
              </div>
              <p className={`text-sm font-medium leading-relaxed ${isDark ? 'text-white/80' : 'text-slate-800'}`}>
                {story.story}
              </p>
              {story.acceptanceCriteria.length > 0 && (
                <div className={`mt-2.5 pt-2.5 border-t ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
                  <p className={`text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                    Acceptance Criteria
                  </p>
                  <ul className="space-y-1">
                    {story.acceptanceCriteria.map((c) => (
                      <li key={c} className={`flex items-start gap-2 text-sm ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                        <CheckCircle size={14} className={`mt-0.5 shrink-0 ${isDark ? 'text-primary/70' : 'text-primary'}`} />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
              isDark ? 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/30' : 'bg-purple-100 text-purple-700 ring-2 ring-white'
            }`} title={story.persona}>
              {personaInitials}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default UserStoriesWidget;
